import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EoHttpClient, IHttpParams } from 'ngx-eo-web-components';
import { BehaviorSubject, catchError, map, Observable, of, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { STATUS } from '../../modules/core/models/loading-status.enum';
import {
  IToken,
  LoginResponse,
} from '../../modules/core/models/token-response.model';
import { jwtDecode } from 'jwt-decode';
import { IUserResetPasswordPayload } from '../../modules/core/models/user-reset-password.model';
import { ApiCallHandler } from '../../modules/core/models/apiCallHandler';
import { User } from '../../modules/core/models/user.model';
import { CoreService } from '../../modules/core/core.service';
import { IAuthConfirmBindingModel } from '../../modules/core/models/auth-confirm-bind.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public userResetPasswordCallHandler = new ApiCallHandler<User | null>();
  public userEmailConfirmCallHandler = new ApiCallHandler<User | null>();
  public updatePasswordCallHandler = new ApiCallHandler();

  public userDataCallHandler = new ApiCallHandler<any | null>();
  public roles$ = new BehaviorSubject<string | null>(null);
  public roleLiterals$ = new BehaviorSubject<string | null>(null);

  constructor(
    private http: EoHttpClient,
    private coreService: CoreService,
    private router: Router
  ) { }

  public RegisterSuccess$ = false;
  public tokenError$ = new BehaviorSubject<string | null>(null);
  public tokenStatus$ = new BehaviorSubject<STATUS>(STATUS.pending);
  public token$ = new BehaviorSubject<IToken | null>(null);
  public checkAFMStatus$ = new Subject<string>();
  public loginStatus$ = new BehaviorSubject<STATUS>(STATUS.pending);
  public loginSuccess$ = new BehaviorSubject<any>(null);
  public loginError$ = new BehaviorSubject<any>(null);
  public saveRegister$ = new Subject<string>();
  public forgotPasswordCh = new ApiCallHandler<void>();

  showPwdModal$ = new BehaviorSubject<boolean>(false);
  pwdDaysLeft$ = new BehaviorSubject<number>(-1);
  changePwdNext$ = new BehaviorSubject<boolean>(false);

  init() {
    this.userResetPasswordCallHandler.init();
    this.userEmailConfirmCallHandler.init();
    this.updatePasswordCallHandler.init();
    this.userDataCallHandler.init();
  }

  hasRoles(userRoles: string[]): boolean {
    const token = this.token$.getValue();
    if (!token || !token.role) {
      return false;
    }

    const roles = Array.isArray(token.role) ? token.role : [token.role];

    return userRoles.some((role) => {
      if (role.startsWith('!')) {
        const strippedRole = role.substring(1).trim();
        return !roles.includes(strippedRole);
      }
      return roles.includes(role);
    });
  }

  getUserId(): any {
    const token = this.token$.getValue();
    if (token) {
      return token.nameid;
    }
    return false;
  }

  fetchToken(username: string, password: string) {
    const body = {
      username: username,
      password: password,
    };

    const params: IHttpParams = {
      url: `${environment.apiUrl}/v1/Auth/login`,
      body: body,
      status$: this.loginStatus$,
      success$: this.loginSuccess$,
      error$: this.loginError$,
    };

    this.http.post<LoginResponse>(params).subscribe({
      next: (success) => {
        this.setToken(success?.token);
      },
      error: (error) => {
        this.loginStatus$.next(STATUS.error); //STATUS.error);
        this.tokenStatus$.next(STATUS.error);
        this.tokenError$.next(error.message);
      },
    });
  }

  resetPassword(payload: IUserResetPasswordPayload) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Auth/reset-password',
      this.userResetPasswordCallHandler,
      payload
    );
    return this.http.post<User>(param);
  }

  confirmEmail(payload: IAuthConfirmBindingModel) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Auth/confirm',
      this.userEmailConfirmCallHandler,
      payload
    );
    return this.http.post<User>(param);
  }

  requestResetPassword(userId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Auth/request-reset-password?userId=${userId}`,
      this.userResetPasswordCallHandler
    );
    return this.http.get<User>(param);
  }

  requestConfirmEmail(userId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/Auth/request-confirm-email/${userId}`,
      this.userEmailConfirmCallHandler
    );
    return this.http.get<User>(param);
  }

  setToken(token: string) {
    if (!token) {
      this.token$.next(null);
      this.tokenStatus$.next(STATUS.error);
      this.tokenError$.next('Empty token provided');
      return;
    }

    if (token && token.indexOf('.') === -1) {
      localStorage.setItem('ekapyIntegrationSystemDtToken', JSON.stringify(token));
      this.token$.next(null);
      this.tokenStatus$.next(STATUS.success);
      return;
    }

    try {
      localStorage.setItem('ekapyIntegrationSystemDtToken', JSON.stringify(token));
      let decodedToken: any = jwtDecode(token);

      this.setRoleBasedOnPrivacy(decodedToken);

      const show = decodedToken.ShowPasswordExpirationModal === 'true';
      const days = +decodedToken.PasswordExpirationDays;
      const changeNext = decodedToken.ChangePasswordOnNextUserLogin === 'true';
      this.pwdDaysLeft$.next(isNaN(days) ? -1 : days);
      this.changePwdNext$.next(changeNext);
      this.showPwdModal$.next(show);

      this.token$.next(decodedToken);
      this.tokenStatus$.next(STATUS.success);
    } catch (error) {
      this.token$.next(null);
      this.tokenStatus$.next(STATUS.success);
    }
  }

  logout() {
    var isCitizen = this.hasRoles(['Citizen']);
    localStorage.clear();
    this.token$.next(null);
    this.tokenError$.next(null);

    this.showPwdModal$.next(false);
    this.pwdDaysLeft$.next(-1);
    this.changePwdNext$.next(false);
    this.clearLastWarnDays();

    if (isCitizen) {
      window.location.assign(`${environment.apiUrl}/v1/GsisOAuth2/logout`);
    } else {
      this.router.navigate(['login'], { queryParams: { ref: 'so' } });
    }
  }

  hydrate() {
    let tokenStr = localStorage.getItem('ekapyIntegrationSystemDtToken');
    if (!tokenStr) return;

    try {
      let token = JSON.parse(tokenStr);
      if (!token || typeof token !== 'string' || token.indexOf('.') === -1) {
        this.token$.next(null);
        return;
      }

      let decodedToken: any = jwtDecode<IToken>(token);
      this.setRoleBasedOnPrivacy(decodedToken);
      this.token$.next(decodedToken);

      const show = decodedToken.ShowPasswordExpirationModal === 'true';
      const days = + decodedToken.PasswordExpirationDays;
      const changeNext = decodedToken.ChangePasswordOnNextUserLogin === 'true';
      this.pwdDaysLeft$.next(isNaN(days) ? -1 : days);
      this.changePwdNext$.next(changeNext);
      this.showPwdModal$.next(show);
    } catch (error) {
      this.token$.next(null);
    }
  }

  private setRoleBasedOnPrivacy(decodedToken: IToken) {
    if (Array.isArray(decodedToken.role)) {
      const updatedRoles = decodedToken.role.map(role => {
        if (role === 'HospitalAdmin' || role === 'ClinicAdmin') {
          let isPrivate = false;
          if (decodedToken.isPrivate != null) {
            if (typeof decodedToken.isPrivate === 'boolean') {
              isPrivate = decodedToken.isPrivate;
            } else if (typeof decodedToken.isPrivate === 'string') {
              isPrivate = decodedToken.isPrivate.toLowerCase() === 'true';
            }
          }
          return isPrivate ? `Private${role}` : role;
        }
        return role;
      });
      decodedToken.role = updatedRoles;
    }
  }

  private readonly warnKey = 'lastPwdWarnDays';

  getLastWarnDays(): number {
    const val = localStorage.getItem(this.warnKey);
    return val ? +val : Number.NaN;
  }

  setLastWarnDays(days: number) {
    localStorage.setItem(this.warnKey, String(days));
  }

  clearLastWarnDays() {
    localStorage.removeItem(this.warnKey);
  }


  updatePassword(newUserPswDto: any) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/Auth/update-user-password',
      this.updatePasswordCallHandler,
      newUserPswDto
    );
    return this.http.post(param);
  }


  forgotPassword(payload: { username: string, email: string }): Observable<void> {
    const params: IHttpParams = this.coreService.initCall(
      '/v1/Auth/forgot-password',
      this.forgotPasswordCh,
      payload
    );

    return this.http.post<void>(params);
  }

}
